/**
 * Local-dev presets for the estate planning post-purchase surface.
 * Priority (development only): `REACT_APP_ESTATE_PLANNING_DEV_MOCK_SCENARIO` in config.js,
 * then hash `?epMock=`, then persisted localStorage.
 *
 * localStorage is only a mirror of one of those two authoritative sources — it carries the
 * active scenario across in-app navigations that drop the hash query. Boot reconciliation in
 * `estatePlanningDevMock` clears it when neither config.js nor `?epMock=` names a scenario,
 * so `__dspEstatePlanningMock.apply` writes the hash param to survive its own reload.
 */
import type {EstateDocumentsGenerationStatus, EstateDocumentsResponse, EstateSession} from './types';
import {
  ESTATE_DEV_MOCK_SCENARIO_URL_PARAM,
  getEstateDevMockHashParam,
  setEstateDevMockHashParam,
} from './estateDevMockHashQuery';
import {applyEstatePlanningEligibilityMockMode} from './estatePlanningEligibilityMock';
import {MOCK_SESSION_ID, MOCK_SESSION_ID_TRUST} from './mockData';

const ALL_QUESTIONNAIRE_SECTION_IDS = [
  'section_getting_started',
  'section_children',
  'section_guardians',
  'section_health',
  'section_assets',
  'section_trust_info',
  'section_pour_over_will',
  'section_spouse_health',
  'section_spouse_pour_over_will',
] as const;

const MOCK_DOCUMENTS_UPDATED_AT = '2026-07-12T14:30:00Z';
const MOCK_NOTARIZE_CONFIRMED_AT = '2026-01-14T12:00:00Z';

export const POST_PURCHASE_MOCK_SCENARIO_STORAGE_KEY = 'dsp.estatePlanning.mockScenario';

export type PostPurchaseMockScenarioId =
  | 'active-getting-started'
  | 'active-in-progress'
  | 'documents-will'
  | 'documents-trust'
  | 'documents-failed'
  | 'official-will'
  | 'official-trust';

export const POST_PURCHASE_MOCK_SCENARIO_IDS: PostPurchaseMockScenarioId[] = [
  'active-getting-started',
  'active-in-progress',
  'documents-will',
  'documents-trust',
  'documents-failed',
  'official-will',
  'official-trust',
];

type PostPurchaseMockScenario = {
  label: string;
  session: EstateSession;
  documents: EstateDocumentsResponse;
};

function mockSessionIdForPackageType(packageType: EstateSession['packageType']): string {
  return packageType === 'TRUST' ? MOCK_SESSION_ID_TRUST : MOCK_SESSION_ID;
}

function buildDocumentsResponse(
  packageType: EstateSession['packageType'],
  status: EstateDocumentsGenerationStatus
): EstateDocumentsResponse {
  return {
    sessionId: mockSessionIdForPackageType(packageType),
    packageType,
    status,
    version: 3,
    updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
    documents:
      status === 'COMPLETED'
        ? [
            {
              docType: 'last-will-and-testament',
              displayName: 'Last will & testament',
              format: 'PDF',
              fileName: 'last-will-and-testament.pdf',
              filePath: `estate-planning/${packageType.toLowerCase()}-package/v3/last-will-and-testament.pdf`,
              nspNodeId: 'nsp-node-will-001',
              updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
              downloadUrl: 'https://example.com/mock/last-will-and-testament.pdf',
            },
            {
              docType: 'advance-health-directive',
              displayName: 'Advance health directive',
              format: 'PDF',
              fileName: 'advance-health-directive.pdf',
              filePath: `estate-planning/${packageType.toLowerCase()}-package/v3/advance-health-directive.pdf`,
              nspNodeId: 'nsp-node-health-001',
              updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
              downloadUrl: 'https://example.com/mock/advance-health-directive.pdf',
            },
            ...(packageType === 'TRUST'
              ? [
                  {
                    docType: 'revocable-living-trust',
                    displayName: 'Revocable living trust',
                    format: 'PDF' as const,
                    fileName: 'revocable-living-trust.pdf',
                    filePath: 'estate-planning/trust-package/v3/revocable-living-trust.pdf',
                    nspNodeId: 'nsp-node-trust-001',
                    updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
                    downloadUrl: 'https://example.com/mock/revocable-living-trust.pdf',
                  },
                  {
                    docType: 'pour-over-will',
                    displayName: 'Pour-over will',
                    format: 'PDF' as const,
                    fileName: 'pour-over-will.pdf',
                    filePath: 'estate-planning/trust-package/v3/pour-over-will.pdf',
                    nspNodeId: 'nsp-node-pour-over-001',
                    updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
                    downloadUrl: 'https://example.com/mock/pour-over-will.pdf',
                  },
                ]
              : []),
          ]
        : [],
  };
}

function buildActiveSession(packageType: EstateSession['packageType'], completedSectionIds: string[]): EstateSession {
  const lastCompletedSectionId = completedSectionIds[completedSectionIds.length - 1] ?? null;

  return {
    id: mockSessionIdForPackageType(packageType),
    status: 'ACTIVE',
    state: 'TX',
    packageType,
    questionnaireCatalogVersion: '2026.07.01',
    progress: {
      lastCompletedSectionId,
      completedSectionIds,
      questionnaireComplete: false,
      questionnaireCompletedAt: null,
    },
    jobDetails: null,
    version: 7,
    createdAt: '2026-07-08T07:10:00Z',
    updatedAt: '2026-07-08T07:10:00Z',
  };
}

function buildDocumentsGeneratedSession(
  packageType: EstateSession['packageType'],
  withNotarize: boolean
): EstateSession {
  return {
    id: mockSessionIdForPackageType(packageType),
    status: 'DOCUMENT_GENERATION_COMPLETE',
    state: 'TX',
    packageType,
    questionnaireCatalogVersion: '2026.07.01',
    progress: {
      lastCompletedSectionId: ALL_QUESTIONNAIRE_SECTION_IDS[ALL_QUESTIONNAIRE_SECTION_IDS.length - 1],
      completedSectionIds: [...ALL_QUESTIONNAIRE_SECTION_IDS],
      questionnaireComplete: true,
      questionnaireCompletedAt: '2026-07-10T12:00:00Z',
    },
    jobDetails: {
      jobId: `mock-${packageType.toLowerCase()}-job`,
      jobStatus: 'COMPLETED',
      prefixPath: packageType === 'TRUST' ? '/Estate/Trusts/mock' : '/Estate/Wills/mock',
    },
    version: 12,
    createdAt: '2026-07-08T07:10:00Z',
    updatedAt: MOCK_DOCUMENTS_UPDATED_AT,
    ...(withNotarize
      ? {postGenerationSteps: {notarization: {status: 'COMPLETED', completedAt: MOCK_NOTARIZE_CONFIRMED_AT}}}
      : {}),
  };
}

const POST_PURCHASE_MOCK_SCENARIOS: Record<PostPurchaseMockScenarioId, PostPurchaseMockScenario> = {
  'active-getting-started': {
    label: 'ACTIVE — getting started',
    session: buildActiveSession('WILL', []),
    documents: buildDocumentsResponse('WILL', 'QUEUED'),
  },
  'active-in-progress': {
    label: 'ACTIVE — in progress',
    session: buildActiveSession('WILL', ['section_getting_started']),
    documents: buildDocumentsResponse('WILL', 'QUEUED'),
  },
  'documents-will': {
    label: 'DOCUMENTS_GENERATED — will (notarize hero)',
    session: buildDocumentsGeneratedSession('WILL', false),
    documents: buildDocumentsResponse('WILL', 'COMPLETED'),
  },
  'documents-trust': {
    label: 'DOCUMENTS_GENERATED — trust (notarize hero)',
    session: buildDocumentsGeneratedSession('TRUST', false),
    documents: buildDocumentsResponse('TRUST', 'COMPLETED'),
  },
  'documents-failed': {
    label: 'DOCUMENTS_GENERATED — generation failed (info locked)',
    session: buildDocumentsGeneratedSession('TRUST', false),
    documents: buildDocumentsResponse('TRUST', 'FAILED'),
  },
  'official-will': {
    label: 'DOCUMENTS_GENERATED + notarize — will (official hero)',
    session: buildDocumentsGeneratedSession('WILL', true),
    documents: buildDocumentsResponse('WILL', 'COMPLETED'),
  },
  'official-trust': {
    label: 'DOCUMENTS_GENERATED + notarize — trust (official hero)',
    session: buildDocumentsGeneratedSession('TRUST', true),
    documents: buildDocumentsResponse('TRUST', 'COMPLETED'),
  },
};

export function isPostPurchaseMockScenarioId(value: string | null | undefined): value is PostPurchaseMockScenarioId {
  return Boolean(value && POST_PURCHASE_MOCK_SCENARIO_IDS.includes(value as PostPurchaseMockScenarioId));
}

/** Reads `REACT_APP_ESTATE_PLANNING_DEV_MOCK_SCENARIO` from runtime config (development only). */
export function getConfiguredPostPurchaseMockScenarioId(): PostPurchaseMockScenarioId | null {
  const configuredScenarioId = window.REACT_APP_ESTATE_PLANNING_DEV_MOCK_SCENARIO;
  return isPostPurchaseMockScenarioId(configuredScenarioId) ? configuredScenarioId : null;
}

/** Resolves the active dev mock scenario: config.js, then `?epMock=`, then localStorage. */
export function resolvePostPurchaseMockScenarioId(): PostPurchaseMockScenarioId | null {
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const configuredScenarioId = getConfiguredPostPurchaseMockScenarioId();
  if (configuredScenarioId) {
    return configuredScenarioId;
  }

  const urlScenarioId = getEstateDevMockHashParam(ESTATE_DEV_MOCK_SCENARIO_URL_PARAM);
  if (isPostPurchaseMockScenarioId(urlScenarioId)) {
    return urlScenarioId;
  }

  const storedScenarioId = window.localStorage.getItem(POST_PURCHASE_MOCK_SCENARIO_STORAGE_KEY);
  return isPostPurchaseMockScenarioId(storedScenarioId) ? storedScenarioId : null;
}

export function isPostPurchaseMockEnabled(): boolean {
  return resolvePostPurchaseMockScenarioId() !== null;
}

/** Maps a post-purchase session mock scenario to the matching product-feature eligibility mode. */
export function inferPostPurchaseMockEligibilityMode(
  scenarioId: PostPurchaseMockScenarioId
): 'post-purchase-will' | 'post-purchase-trust' {
  if (scenarioId === 'documents-trust' || scenarioId === 'documents-failed' || scenarioId === 'official-trust') {
    return 'post-purchase-trust';
  }

  return 'post-purchase-will';
}

export function getPostPurchaseMockScenario(scenarioId: PostPurchaseMockScenarioId): PostPurchaseMockScenario {
  return POST_PURCHASE_MOCK_SCENARIOS[scenarioId];
}

export type ApplyPostPurchaseMockScenarioResult = {
  scenarioId: PostPurchaseMockScenarioId;
  label: string;
};

export function applyPostPurchaseMockScenario(
  scenarioId: PostPurchaseMockScenarioId,
  {
    setMockSession,
    setMockDocuments,
  }: {
    setMockSession: (_session: EstateSession) => void;
    setMockDocuments: (_documents: EstateDocumentsResponse) => void;
  }
): ApplyPostPurchaseMockScenarioResult {
  const scenario = getPostPurchaseMockScenario(scenarioId);
  setMockSession({...scenario.session});
  setMockDocuments({...scenario.documents, documents: [...scenario.documents.documents]});
  window.localStorage.setItem(POST_PURCHASE_MOCK_SCENARIO_STORAGE_KEY, scenarioId);
  if (process.env.NODE_ENV === 'development') {
    applyEstatePlanningEligibilityMockMode(inferPostPurchaseMockEligibilityMode(scenarioId));
  }

  return {scenarioId, label: scenario.label};
}

/** Applies the active dev scenario before React Query hooks resolve mock payloads. */
export function initPostPurchaseMockFromUrl({
  setMockSession,
  setMockDocuments,
}: {
  setMockSession: (_session: EstateSession) => void;
  setMockDocuments: (_documents: EstateDocumentsResponse) => void;
}): ApplyPostPurchaseMockScenarioResult | null {
  const scenarioId = resolvePostPurchaseMockScenarioId();
  if (!scenarioId) {
    return null;
  }

  return applyPostPurchaseMockScenario(scenarioId, {setMockSession, setMockDocuments});
}

export function registerPostPurchaseMockDevConsole({
  setMockSession,
  setMockDocuments,
}: {
  setMockSession: (_session: EstateSession) => void;
  setMockDocuments: (_documents: EstateDocumentsResponse) => void;
}): void {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  window.__dspEstatePlanningMock = {
    scenarios: POST_PURCHASE_MOCK_SCENARIO_IDS.map((id) => ({
      id,
      label: POST_PURCHASE_MOCK_SCENARIOS[id].label,
    })),
    current: resolvePostPurchaseMockScenarioId(),
    apply(scenarioId: string) {
      if (!isPostPurchaseMockScenarioId(scenarioId)) {
        // eslint-disable-next-line no-console
        console.error(
          `[estate-planning mock] Unknown scenario "${scenarioId}". ` +
            `Use one of: ${POST_PURCHASE_MOCK_SCENARIO_IDS.join(', ')}`
        );
        return;
      }

      applyPostPurchaseMockScenario(scenarioId, {setMockSession, setMockDocuments});
      setEstateDevMockHashParam(ESTATE_DEV_MOCK_SCENARIO_URL_PARAM, scenarioId);
      window.location.reload();
    },
    clear() {
      window.localStorage.removeItem(POST_PURCHASE_MOCK_SCENARIO_STORAGE_KEY);
      setEstateDevMockHashParam(ESTATE_DEV_MOCK_SCENARIO_URL_PARAM, null);
      window.location.reload();
    },
  };
}
